import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { emitSignal, createLessonCompletedSignal } from '@/lib/signals';
import { uuidToSlug } from '@/lib/subject-mapping';

// Query keys
export const lessonProgressKeys = {
  all: ['lesson-progress'] as const,
  user: (userId: string) => [...lessonProgressKeys.all, userId] as const,
};

/**
 * Fetch all lesson completion records for the current user.
 * Returns a map of lessonId → completed_at (ISO string or null).
 * A lesson is "completed" if completed_at is non-null.
 */
export function useLessonProgress() {
  const { user } = useAuth();

  return useQuery({
    queryKey: lessonProgressKeys.user(user?.id || ''),
    queryFn: async (): Promise<Record<string, string | null>> => {
      if (!user) return {};

      const { data, error } = await supabase
        .from('lesson_progress')
        .select('lesson_id, completed_at')
        .eq('user_id', user.id);

      if (error) throw error;

      const map: Record<string, string | null> = {};
      for (const row of data || []) {
        map[row.lesson_id] = row.completed_at;
      }
      return map;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

interface ToggleLessonInput {
  lessonId: string;
  courseId: string;
  courseType: string; // 'nextgen' | 'standard'
  isCompleted: boolean; // current state — true means "unmark", false means "mark complete"
  lessonTitle?: string;
  moduleId?: string;
}

/**
 * Toggle lesson completion (mark/unmark).
 * On completion: upserts lesson_progress with completed_at = now(),
 * resolves subject via course_subjects, and emits a course.lesson_completed signal.
 * On un-completion: updates completed_at to null (no signal emitted).
 */
export function useToggleLessonCompletion() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ToggleLessonInput) => {
      if (!user) throw new Error('Not authenticated');

      const { lessonId, courseId, courseType, isCompleted, lessonTitle, moduleId } = input;
      const nowMarking = !isCompleted; // toggling: if currently completed, we're unmarking

      if (nowMarking) {
        // Mark complete: upsert with completed_at = now()
        const { error } = await supabase
          .from('lesson_progress')
          .upsert(
            {
              user_id: user.id,
              lesson_id: lessonId,
              completed_at: new Date().toISOString(),
            },
            { onConflict: 'user_id,lesson_id' }
          );
        if (error) throw error;

        // Resolve subject from course_subjects junction table
        let subject: string | undefined;
        const { data: courseSubjects } = await supabase
          .from('course_subjects')
          .select('subject_id')
          .eq('course_id', courseId)
          .limit(1);

        if (courseSubjects?.length) {
          subject = uuidToSlug(courseSubjects[0].subject_id) ?? undefined;
        }

        // Emit intelligence signal (fire-and-forget)
        emitSignal(createLessonCompletedSignal(user.id, {
          lesson_id: lessonId,
          course_id: courseId,
          course_type: courseType,
          subject,
          lesson_title: lessonTitle,
          module_id: moduleId,
        }));

        return { action: 'completed' as const };
      } else {
        // Unmark: set completed_at to null
        const { error } = await supabase
          .from('lesson_progress')
          .update({ completed_at: null })
          .eq('user_id', user.id)
          .eq('lesson_id', lessonId);
        if (error) throw error;

        return { action: 'uncompleted' as const };
      }
    },
    // Optimistic update for instant UI
    onMutate: async (input) => {
      const queryKey = lessonProgressKeys.user(user?.id || '');
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Record<string, string | null>>(queryKey);

      queryClient.setQueryData<Record<string, string | null>>(queryKey, (old) => {
        const next = { ...old };
        if (input.isCompleted) {
          // Unmarking
          next[input.lessonId] = null;
        } else {
          // Marking complete
          next[input.lessonId] = new Date().toISOString();
        }
        return next;
      });

      return { previous };
    },
    onError: (_err, _input, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(lessonProgressKeys.user(user?.id || ''), context.previous);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: lessonProgressKeys.user(user?.id || '') });
    },
  });
}
